import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import BrandLogo from '../components/shared/BrandLogo';
import { BRAND_HERO_GRADIENT } from '../components/shared/SectionTitle';

const LAST_UPDATED = 'February 17, 2026';
const EFFECTIVE_DATE = 'February 17, 2026';

const SECTIONS = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'definitions', title: '2. Definitions' },
  { id: 'account', title: '3. Account Registration' },
  { id: 'service', title: '4. The Service' },
  { id: 'acceptable-use', title: '5. Acceptable Use' },
  { id: 'platform-integrations', title: '6. Platform Integrations' },
  { id: 'subscriptions', title: '7. Subscriptions and Billing' },
  { id: 'intellectual-property', title: '8. Intellectual Property' },
  { id: 'disclaimers', title: '9. Disclaimers' },
  { id: 'limitation-of-liability', title: '10. Limitation of Liability' },
  { id: 'indemnification', title: '11. Indemnification' },
  { id: 'termination', title: '12. Termination' },
  { id: 'dispute-resolution', title: '13. Dispute Resolution' },
  { id: 'governing-law', title: '14. Governing Law' },
  { id: 'changes', title: '15. Changes to Terms' },
  { id: 'contact', title: '16. Contact Us' },
];

const TermsOfServicePage = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
      const sectionEls = SECTIONS.map(s => document.getElementById(s.id));
      for (let i = sectionEls.length - 1; i >= 0; i--) {
        const el = sectionEls[i];
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="md" />
          </Link>
          <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Home</Link>
            <ChevronRightIcon className="w-3 h-3" />
            <span className="text-gray-900 dark:text-white font-medium">Terms of Service</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-28">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                Table of Contents
              </p>
              <nav className="space-y-1">
                {SECTIONS.map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`block text-sm py-1.5 px-3 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-dark-800'
                    }`}
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
              <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">Last Updated</p>
                <p className="text-xs text-blue-600 dark:text-blue-300">{LAST_UPDATED}</p>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="mb-10">
              <div className="h-1.5 w-24 rounded-full mb-5" style={{ background: BRAND_HERO_GRADIENT }} aria-hidden />
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Terms of Service</h1>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Effective Date:</strong> {EFFECTIVE_DATE}
              </p>
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Please read these Terms carefully.</strong> By creating an account or using OmniConnX, you agree to
                  be bound by these Terms of Service. If you do not agree, do not use the Service.
                </p>
              </div>
            </div>

            <div className="space-y-12 text-gray-700 dark:text-gray-300 leading-relaxed">

              {/* Section 1 */}
              <section id="acceptance">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">1. Acceptance of Terms</h2>
                <p className="mb-4">
                  These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your")
                  and OmniConnX, Inc. ("OmniConnX," "we," "us," or "our") governing your access to and use of the OmniConnX
                  platform, website, APIs, and all related services (collectively, the "Service").
                </p>
                <p className="mb-4">
                  By accessing or using the Service, you represent that:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2">
                  <li>You are at least 13 years of age (or 16 in certain EEA jurisdictions).</li>
                  <li>You have the legal authority to enter into these Terms on behalf of yourself or the entity you represent.</li>
                  <li>Your use of the Service complies with all applicable laws and regulations in your jurisdiction.</li>
                  <li>You have read, understood, and agree to be bound by these Terms and our{' '}
                    <Link to="/privacy" className="text-violet-600 dark:text-violet-400 hover:underline">Privacy Policy</Link>.
                  </li>
                </ul>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 2 */}
              <section id="definitions">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">2. Definitions</h2>
                <div className="space-y-3">
                  {[
                    { term: 'Account', def: 'Your registered user account on the OmniConnX platform.' },
                    { term: 'Content', def: 'Any data, text, analytics, or other material made available through or generated by the Service.' },
                    { term: 'Connected Platform', def: 'A third-party social media or content platform (e.g., YouTube, TikTok, Instagram, Twitter/X) linked to your Account via OAuth.' },
                    { term: 'Platform Data', def: 'Analytics, metrics, and monetization data retrieved from a Connected Platform through authorized API access.' },
                    { term: 'Subscription', def: 'A paid plan granting access to certain features of the Service during a billing period.' },
                    { term: 'User Content', def: 'Any information, notes, or custom data you input into the Service (excluding Platform Data retrieved automatically).' },
                  ].map(({ term, def }) => (
                    <div key={term} className="flex gap-3">
                      <span className="font-semibold text-gray-900 dark:text-white min-w-[160px]">"{term}"</span>
                      <span>{def}</span>
                    </div>
                  ))}
                </div>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 3 */}
              <section id="account">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">3. Account Registration</h2>
                <p className="mb-4">
                  To use OmniConnX, you must create an Account. You agree to:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li>Provide accurate, current, and complete information during registration.</li>
                  <li>Maintain and update your account information to keep it accurate and current.</li>
                  <li>Keep your password confidential and not share it with any third party.</li>
                  <li>Immediately notify us at{' '}
                    <a href="mailto:security@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                      security@omniconnx.com
                    </a>{' '}
                    if you suspect unauthorized access to your Account.
                  </li>
                  <li>Be solely responsible for all activities that occur under your Account.</li>
                </ul>
                <p className="mb-4">
                  You may not create an Account if you have previously been terminated from the Service. You may not
                  create multiple Accounts to circumvent usage limits or a suspension. One Account per person or entity unless
                  you have an Enterprise plan with explicit written permission from OmniConnX.
                </p>
                <p>
                  OmniConnX reserves the right to refuse registration, suspend, or terminate Accounts at its sole discretion,
                  with or without cause and with or without notice, subject to Section 12 (Termination).
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 4 */}
              <section id="service">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">4. The Service</h2>
                <p className="mb-4">
                  OmniConnX provides a creator analytics and business intelligence platform that allows content creators to:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li>Connect social media platforms via OAuth and retrieve analytics data.</li>
                  <li>View unified performance dashboards across connected platforms.</li>
                  <li>Monitor audience health, engagement metrics, and revenue trends.</li>
                  <li>Detect anomalies and receive automated alerts about metric changes.</li>
                  <li>Track and reconcile revenue from multiple sources.</li>
                  <li>Manage brand deals and sponsorship agreements.</li>
                  <li>Access AI-powered insights and recommendations.</li>
                </ul>
                <p className="mb-4">
                  <strong>Service Availability:</strong> We aim to maintain 99.5% uptime (excluding scheduled maintenance),
                  but do not guarantee uninterrupted access. We will provide advance notice of planned maintenance via email
                  or status page (status.omniconnx.com) where possible. Scheduled maintenance typically occurs outside of
                  US business hours (9 AM–6 PM PT, Monday–Friday).
                </p>
                <p>
                  <strong>Beta Features:</strong> Features labeled "Beta," "Preview," or "Early Access" are provided as-is
                  and may be changed or removed without notice. They are excluded from uptime commitments.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 5 */}
              <section id="acceptable-use">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">5. Acceptable Use</h2>
                <p className="mb-4">You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree <strong>not</strong> to:</p>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    'Violate any applicable law, regulation, or third-party rights',
                    'Impersonate any person or entity or falsely represent your affiliation',
                    'Scrape, crawl, or use automated tools to extract data from the Service beyond normal usage',
                    'Attempt to reverse-engineer, decompile, or disassemble the Service',
                    'Upload or transmit malware, viruses, or any harmful code',
                    'Interfere with or disrupt the integrity or performance of the Service or servers',
                    'Use the Service to provide a competitive product without our written consent',
                    'Resell or sublicense access to the Service to third parties without authorization',
                    'Misuse our APIs beyond your authorized rate limits and permitted use cases',
                    'Use the Service to facilitate spam, phishing, or deceptive practices',
                    'Store or transmit content that is illegal, defamatory, or infringes on intellectual property rights',
                    'Circumvent any technical measures we use to limit access or enforce these Terms',
                  ].map(item => (
                    <div key={item} className="flex gap-2 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-800/30">
                      <span className="text-red-500 mt-0.5 flex-shrink-0">✕</span>
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <p>
                  Violation of these restrictions may result in immediate suspension or termination of your Account and may
                  expose you to civil or criminal liability. OmniConnX reserves the right to investigate suspected violations
                  and cooperate with law enforcement authorities.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 6 */}
              <section id="platform-integrations">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">6. Platform Integrations</h2>
                <p className="mb-4">
                  OmniConnX connects to third-party platforms (YouTube, TikTok, Instagram, Twitter/X) via their official
                  OAuth 2.0 APIs. By connecting a platform:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li>You authorize OmniConnX to access your analytics data through the permissions you grant during the OAuth flow.</li>
                  <li>You represent that you are the authorized owner or administrator of the connected account.</li>
                  <li>You acknowledge that the availability and accuracy of Platform Data depends on the third-party platform's API and is outside OmniConnX's control.</li>
                  <li>You agree to comply with the terms of service of each Connected Platform.</li>
                  <li>You understand that platform API changes may affect the Service's ability to retrieve certain data without prior notice.</li>
                </ul>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 mb-6">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Scope of Access:</strong> OmniConnX requests read-only access to your analytics and monetization data.
                    We do not publish content, send messages, or perform actions on your behalf without explicit, separate
                    authorization. You can review and revoke our access at any time through the Connected Platform's developer
                    settings or through OmniConnX Settings → Integrations (or the Platforms card on the Analytics dashboard).
                  </p>
                </div>
                <p className="mb-4">
                  <strong>YouTube-Specific Terms:</strong> Use of YouTube API data is subject to the{' '}
                  <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline">
                    YouTube Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline">
                    Google Privacy Policy
                  </a>.
                  OmniConnX's use of YouTube API data complies with the YouTube API Services Terms of Service.
                </p>
                <p>
                  <strong>Responsibility:</strong> You are solely responsible for maintaining the security of your platform
                  credentials and for any activity in connected accounts. OmniConnX is not responsible for any actions taken
                  by Connected Platforms or for downtime of third-party APIs.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 7 */}
              <section id="subscriptions">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">7. Subscriptions and Billing</h2>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">7.1 Subscription Plans</h3>
                <p className="mb-6">
                  OmniConnX offers free and paid subscription plans. Paid plans are billed on a monthly or annual basis.
                  Features available at each tier are described on our{' '}
                  <Link to="/pricing" className="text-violet-600 dark:text-violet-400 hover:underline">Pricing page</Link> and
                  may be updated with 30 days' notice to existing subscribers.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">7.2 Free Trial</h3>
                <p className="mb-6">
                  We may offer a free trial period for paid plans. Unless you cancel before the trial ends, your Subscription
                  will automatically convert to a paid plan and your payment method will be charged. You may cancel a free trial
                  at any time before it ends without charge.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">7.3 Payment</h3>
                <p className="mb-4">
                  Payments are processed securely by Stripe, Inc. By providing payment information, you authorize OmniConnX
                  to charge the applicable fees to your payment method. You represent that:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li>You have the legal right to use the payment method provided.</li>
                  <li>The billing information you provide is accurate and complete.</li>
                  <li>You will update billing information to keep it current.</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">7.4 Automatic Renewal</h3>
                <p className="mb-6">
                  Subscriptions automatically renew at the end of each billing period unless you cancel at least 24 hours
                  before the renewal date in Account Settings. We will send a renewal reminder email at least 7 days before
                  the renewal date for annual plans.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">7.5 Price Changes</h3>
                <p className="mb-6">
                  We will provide at least 30 days' advance notice of price increases via email. Price changes take effect at
                  the start of your next billing cycle following the notice period. If you do not agree to a price change, you
                  may cancel your Subscription before it takes effect.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">7.6 Refunds</h3>
                <p className="mb-4">
                  All fees are non-refundable except as required by law or as explicitly stated below:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li><strong>14-Day Money-Back Guarantee:</strong> New paid subscribers may request a full refund within 14 days of the initial payment if not satisfied.</li>
                  <li><strong>Service Outages:</strong> If the Service experiences downtime exceeding 99.5% monthly uptime SLA, you may be eligible for a pro-rated credit (not cash refund).</li>
                  <li><strong>Annual Plans (Early Cancellation):</strong> Annual subscribers who cancel after 14 days receive a pro-rated credit for unused months, applicable to a future subscription.</li>
                </ul>
                <p>
                  To request a refund, contact{' '}
                  <a href="mailto:billing@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    billing@omniconnx.com
                  </a>.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 8 */}
              <section id="intellectual-property">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">8. Intellectual Property</h2>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">8.1 OmniConnX IP</h3>
                <p className="mb-6">
                  The Service, including its design, code, algorithms, AI models, documentation, trademarks (including
                  "OmniConnX"), and all content created by us, is owned by OmniConnX, Inc. and protected by copyright,
                  trademark, and other intellectual property laws. You may not copy, reproduce, distribute, or create
                  derivative works from our materials without our express written permission.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">8.2 License to Use the Service</h3>
                <p className="mb-6">
                  Subject to your compliance with these Terms and payment of applicable fees, OmniConnX grants you a limited,
                  non-exclusive, non-transferable, revocable license to access and use the Service for your internal business
                  purposes as a content creator.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">8.3 Your Data and User Content</h3>
                <p className="mb-6">
                  You retain all ownership rights to your Platform Data and User Content. By using the Service, you grant
                  OmniConnX a limited license to access, process, and display your data solely to provide and improve the
                  Service. We do not claim ownership of your data. We will not use your Platform Data to train AI models
                  without your explicit consent.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">8.4 Feedback</h3>
                <p>
                  If you provide feedback, suggestions, or ideas about the Service, you grant OmniConnX a perpetual,
                  irrevocable, royalty-free license to use such feedback for any purpose without obligation to you. This
                  does not affect your ownership of your Account data.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 9 */}
              <section id="disclaimers">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">9. Disclaimers</h2>
                <div className="bg-gray-50 dark:bg-dark-800 rounded-xl p-6 border border-gray-200 dark:border-dark-600 mb-4">
                  <p className="font-semibold text-gray-900 dark:text-white mb-3 uppercase text-sm tracking-wide">
                    Important — Please Read
                  </p>
                  <p className="mb-4">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
                    IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                    NON-INFRINGEMENT, OR UNINTERRUPTED AVAILABILITY.
                  </p>
                  <p>
                    OMNICONNX DOES NOT WARRANT THAT: (A) THE SERVICE WILL BE ERROR-FREE OR UNINTERRUPTED; (B) ANALYTICS DATA
                    WILL BE ACCURATE, COMPLETE, OR CURRENT; (C) THE SERVICE WILL MEET YOUR SPECIFIC REQUIREMENTS; OR (D) ANY
                    SECURITY MEASURES WILL BE IMPENETRABLE. PLATFORM DATA IS RETRIEVED FROM THIRD-PARTY APIS AND WE MAKE NO
                    REPRESENTATIONS REGARDING ITS ACCURACY OR COMPLETENESS.
                  </p>
                </div>
                <p>
                  The Service is not intended to be relied upon as the sole basis for business decisions. Analytics data,
                  revenue projections, and AI-generated insights are informational only and should be verified independently.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 10 */}
              <section id="limitation-of-liability">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">10. Limitation of Liability</h2>
                <div className="bg-gray-50 dark:bg-dark-800 rounded-xl p-6 border border-gray-200 dark:border-dark-600 mb-4">
                  <p className="mb-4">
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, OMNICONNX AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
                    AGENTS, AND SUPPLIERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                    DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES, ARISING FROM OR
                    RELATED TO YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY
                    OF SUCH DAMAGES.
                  </p>
                  <p>
                    OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR THE SERVICE
                    WILL NOT EXCEED THE GREATER OF: (A) THE TOTAL FEES YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR
                    (B) ONE HUNDRED U.S. DOLLARS ($100).
                  </p>
                </div>
                <p>
                  Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities. In such
                  jurisdictions, our liability will be limited to the maximum extent permitted by law.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 11 */}
              <section id="indemnification">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">11. Indemnification</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless OmniConnX, its affiliates, officers, directors, employees,
                  and agents from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable
                  attorneys' fees) arising from or related to: (a) your use of the Service; (b) your violation of these Terms;
                  (c) your violation of any third-party right, including intellectual property rights or privacy rights; or
                  (d) any content you submit through the Service. OmniConnX reserves the right, at its own expense, to assume
                  exclusive defense of any matter subject to indemnification by you.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 12 */}
              <section id="termination">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">12. Termination</h2>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">12.1 Termination by You</h3>
                <p className="mb-6">
                  You may cancel your Account at any time through Settings → Account → Delete Account, or by emailing{' '}
                  <a href="mailto:support@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    support@omniconnx.com
                  </a>.
                  Cancellation takes effect at the end of your current billing period (you retain access until then).
                  We will delete your data within 30 days of account deletion, subject to legal retention requirements.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">12.2 Termination by OmniConnX</h3>
                <p className="mb-4">
                  OmniConnX may suspend or terminate your Account:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li><strong>For cause:</strong> Immediately, without notice, if you materially breach these Terms, engage in fraud, or pose a security risk.</li>
                  <li><strong>For convenience:</strong> With 30 days' written notice. If we terminate for convenience, we will provide a pro-rated refund for unused subscription time.</li>
                  <li><strong>Non-payment:</strong> With 7 days' notice if fees remain unpaid after the due date.</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">12.3 Effect of Termination</h3>
                <p>
                  Upon termination, your right to access the Service immediately ceases. Sections 8 (IP), 9 (Disclaimers),
                  10 (Limitation of Liability), 11 (Indemnification), and 13 (Dispute Resolution) survive termination.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 13 */}
              <section id="dispute-resolution">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">13. Dispute Resolution</h2>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">13.1 Informal Resolution</h3>
                <p className="mb-6">
                  Before initiating any formal dispute, you agree to contact us at{' '}
                  <a href="mailto:legal@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    legal@omniconnx.com
                  </a>{' '}
                  and give us 30 days to resolve the issue informally. Most disputes are resolved this way.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">13.2 Binding Arbitration</h3>
                <p className="mb-4">
                  If informal resolution fails, any dispute arising from these Terms or the Service (except as noted in 13.3)
                  will be resolved by binding arbitration administered by JAMS under its Streamlined Arbitration Rules.
                  Arbitration will be conducted in San Francisco, California, or virtually, at each party's option.
                </p>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Class Action Waiver:</strong> YOU AGREE TO RESOLVE DISPUTES INDIVIDUALLY. YOU WAIVE THE RIGHT
                    TO PARTICIPATE IN CLASS ACTION LAWSUITS OR CLASS-WIDE ARBITRATION. This waiver does not apply to claims
                    brought under applicable consumer protection laws if required by statute.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">13.3 Exceptions</h3>
                <p>
                  Either party may seek injunctive relief in court for intellectual property infringement, unauthorized access,
                  or imminent harm to the Service. Claims under $10,000 may be brought in small claims court in lieu of arbitration.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 14 */}
              <section id="governing-law">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">14. Governing Law</h2>
                <p className="mb-4">
                  These Terms are governed by the laws of the State of California, United States, without regard to its
                  conflict of laws principles. For matters not subject to arbitration, you consent to the exclusive jurisdiction
                  of state and federal courts located in San Francisco County, California.
                </p>
                <p>
                  <strong>EU/UK Users:</strong> Nothing in these Terms limits your statutory consumer rights under EU or UK law.
                  If you are a consumer resident in the EU or UK, mandatory consumer protection laws of your country of residence
                  may apply.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 15 */}
              <section id="changes">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">15. Changes to Terms</h2>
                <p className="mb-4">
                  We may modify these Terms at any time. For material changes, we will:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-4">
                  <li>Update the "Last Updated" date at the top of this page.</li>
                  <li>Notify registered users via email at least 14 days before the changes take effect.</li>
                  <li>Display a prominent notice on the Service for 30 days after the change.</li>
                </ul>
                <p>
                  Your continued use of the Service after the effective date constitutes acceptance of the updated Terms.
                  If you do not agree to the updated Terms, you must stop using the Service and may cancel your Account
                  before the effective date to receive a pro-rated refund.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 16 */}
              <section id="contact">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">16. Contact Us</h2>
                <p className="mb-6">For questions, concerns, or legal notices, contact us at:</p>
                <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-700">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">General Support</p>
                      <p className="text-sm">
                        <a href="mailto:support@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                          support@omniconnx.com
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Legal / Billing</p>
                      <p className="text-sm">
                        <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                          info@omniconnx.com
                        </a>
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Registered Address</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        OmniConnX, Inc.<br />
                        Milton, ON L9E1S6<br />
                        Canada
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Footer Links */}
              <div className="pt-4 pb-12 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>Related:</span>
                <Link to="/privacy" className="text-violet-600 dark:text-violet-400 hover:underline">Privacy Policy</Link>
                <Link to="/cookies" className="text-violet-600 dark:text-violet-400 hover:underline">Cookie Policy</Link>
                <Link to="/contact" className="text-violet-600 dark:text-violet-400 hover:underline">Contact Us</Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white p-3 rounded-full shadow-lg transition-all z-50"
          aria-label="Back to top"
        >
          <ChevronUpIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default TermsOfServicePage;
